import { reactive } from "vue";
import {
  getEmployeeBadgeCounterProps as getSharedBadgeCounterProps,
  createSetLoading,
  primeDirectoryTableRouterCache,
  resolveDirectoryTableRouterContext,
} from "@components/DirectoriesShared/directoryTableHelpers.js";

let directoryTableViewSingleton = null;

export function useEmployeesDirectoryTableViewState() {
  if (directoryTableViewSingleton) {
    return directoryTableViewSingleton;
  }

  primeDirectoryTableRouterCache();

  const navigateToEmployeeDirectoryDetail = (item, employeeDirectoryTab) => {
    const { route, router } = resolveDirectoryTableRouterContext();
    if (!item?.id || !router || !route) return;
    const fromYourStuffDirectories = route.path.startsWith("/your-stuff/directories");
    const routeName = fromYourStuffDirectories
      ? "YourStuffEmployeeDirectoryDetail"
      : "EmployeeDirectoryDetail";
    router
      .push({
        name: routeName,
        params: { id: String(item.id) },
        query: { employeeDirectoryTab },
      })
      .catch(() => {});
  };

  const state = reactive({
    filters: {
      values: [],
      searchText: "",
      showFilterDialog: false,
      clearSelection: "",
    },
    loading: {
      table: false,
    },
    table: {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    embedded: false,
    showEmployeeBadgesList: false,
    showEmployeeReadinessPath: false,
    selectedEmployee: null,
    /** Your Stuff → Directories: show responsibility drill-down inline (same URL as the table). */
    showResponsibilityDirectoryCard: false,
    selectedResponsibilityId: null,
    /** Snapshot of the responsibilities directory API row when drilling down (rich list payload). */
    selectedResponsibilityDirectoryRow: null,
  });

  const setEmbedded = (value) => {
    state.embedded = !!value;
  };

  const stripYourStuffResponsibilityQuery = () => {
    const { route, router } = resolveDirectoryTableRouterContext();
    if (!router || !route) return;
    const q = { ...route.query };
    if (q.from !== "your-stuff-directories") return;
    delete q.from;
    delete q.resTab;
    delete q.tab;
    delete q.filter;
    router.replace({ path: route.path, query: q }).catch(() => {});
  };

  const openResponsibilityDirectoryInline = (
    resId,
    resTab = "checklist",
    filter = null,
    directoryRow = null
  ) => {
    const { route, router } = resolveDirectoryTableRouterContext();
    if (resId == null || !router || !route) return;
    const fromYourStuffDirectories = route.path.startsWith("/your-stuff/directories");
    if (!state.embedded || !fromYourStuffDirectories) {
      let pushResTab = resTab;
      let pushFilter = filter;
      if (pushResTab === "coaches") {
        pushResTab = "badge-holders";
        pushFilter = pushFilter || "coach";
      } else if (pushResTab === "masters") {
        pushResTab = "badge-holders";
        pushFilter = pushFilter || "master";
      }
      const query =
        pushFilter != null && pushFilter !== ""
          ? { resTab: pushResTab, filter: String(pushFilter) }
          : { resTab: pushResTab };
      router
        .push({
          name: "ResponsibilityDetailChild",
          params: { resId: String(resId) },
          query,
        })
        .catch(() => {});
      return;
    }
    state.showEmployeeBadgesList = false;
    state.showEmployeeReadinessPath = false;
    state.selectedEmployee = null;
    state.selectedResponsibilityId = resId;
    state.selectedResponsibilityDirectoryRow =
      directoryRow && typeof directoryRow === "object" ? { ...directoryRow } : null;
    state.showResponsibilityDirectoryCard = true;
    const query = { ...route.query, from: "your-stuff-directories", resTab };
    if (filter) {
      query.filter = filter;
    } else {
      delete query.filter;
    }
    delete query.tab;
    router.replace({ path: route.path, query }).catch(() => {});
  };

  const closeResponsibilityDirectoryInline = () => {
    state.showResponsibilityDirectoryCard = false;
    state.selectedResponsibilityId = null;
    state.selectedResponsibilityDirectoryRow = null;
    stripYourStuffResponsibilityQuery();
  };

  const resetInlineDirectoryView = () => {
    state.showEmployeeBadgesList = false;
    state.showEmployeeReadinessPath = false;
    state.selectedEmployee = null;
    state.showResponsibilityDirectoryCard = false;
    state.selectedResponsibilityId = null;
    state.selectedResponsibilityDirectoryRow = null;
    stripYourStuffResponsibilityQuery();
  };

  const applyFilters = (filters) => {
    const normalized = filters
      .map((item) => {
        if (item?.value != null) {
          return typeof item === "string" ? item : item.value;
        }
        return null;
      })
      .filter(Boolean);
    state.filters.values = normalized;
    state.filters.showFilterDialog = false;
  };

  const removeFilters = (removedFilter) => {
    state.filters.clearSelection = removedFilter;
  };

  const clearFilters = () => {
    state.filters.values = [];
    state.filters.showFilterDialog = false;
    state.filters.clearSelection = "";
  };

  const getEmployeeCardProps = (item) => ({
    firstName: item?.first_name,
    lastName: item?.last_name,
    position: item?.position,
    photoUrl: item?.avatar,
    availability: item?.availability,
    showAvailability: true,
    showLinkUnderline: false,
    hideAvailabilitySelector: true,
    isHoverEnabled: false,
  });

  const getReadinessBarProps = (item) => ({
    barValue: item?.current_readiness,
    type: "readiness",
  });

  const getEmployeeBadgeCounterProps = (item) => getSharedBadgeCounterProps(item);

  const getReadinessMapButtonProps = (item) => ({
    novice_value: item?.checkpoint?.novice ?? 0,
    apprentice_value: item?.checkpoint?.apprentice ?? 0,
    proficient_value: item?.checkpoint?.professional ?? 0,
    coach_value: item?.checkpoint?.coach ?? 0,
    architect_value: item?.checkpoint?.master ?? 0,
  });

  const onClickBadgeCounter = (item) => {
    state.selectedEmployee = item;
    if (state.embedded) {
      state.showResponsibilityDirectoryCard = false;
      state.selectedResponsibilityId = null;
      stripYourStuffResponsibilityQuery();
      state.showEmployeeBadgesList = true;
      state.showEmployeeReadinessPath = false;
    } else {
      navigateToEmployeeDirectoryDetail(item, "badges");
    }
  };

  const onClickReadinessMap = (item) => {
    state.selectedEmployee = item;
    if (state.embedded) {
      state.showResponsibilityDirectoryCard = false;
      state.selectedResponsibilityId = null;
      stripYourStuffResponsibilityQuery();
      state.showEmployeeReadinessPath = true;
      state.showEmployeeBadgesList = false;
    } else {
      navigateToEmployeeDirectoryDetail(item, "path_to_readiness");
    }
  };

  directoryTableViewSingleton = {
    state,
    setEmbedded,
    resetInlineDirectoryView,
    openResponsibilityDirectoryInline,
    closeResponsibilityDirectoryInline,
    applyFilters,
    removeFilters,
    clearFilters,
    getEmployeeCardProps,
    getReadinessBarProps,
    getEmployeeBadgeCounterProps,
    getReadinessMapButtonProps,
    onClickBadgeCounter,
    onClickReadinessMap,
    setLoading: createSetLoading(state.loading),
  };

  return directoryTableViewSingleton;
}
